/**
 * EDITOR-POINT-MODEL-UNIFY (Fase A, 2026-06-18) — helpers puros do modelo
 * unificado de pontos de uma etapa.
 *
 * Modelo: cada ponto editável de uma etapa (`seg.points[]`) é UM objeto com
 * um `kind`:
 *   • kind === "stop"  → Ponto de paragem (via): obrigatório, marker,
 *                        editável (name/category/description multilingues,
 *                        photos[]). Força o routing.
 *   • kind === "road"  → Ponto de estrada (shaping): opcional, sem marker,
 *                        só condiciona o traçado (via:true ao motor).
 *   • kind ausente     → geometria densa (amostras de um track GPX cru). Não
 *                        é marker nem dot; só forma. (Tracks > ~25 pontos não
 *                        recalculam.)
 *
 * A geometria densa de exibição continua em `seg.routedCoords`.
 * Substitui o antigo par `seg.waypoints` (markers) + `seg.pois` (PDIs).
 *
 * Sem React, sem rede — fácil de testar.
 */

export const STOP = "stop";
export const ROAD = "road";

// Id estável client-side para paragens (chaveia editar/remover/testids).
let _stopSeq = 0;
export function genStopId() {
  _stopSeq += 1;
  return `stop_${Date.now().toString(36)}_${_stopSeq.toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
}

export const isStop = (p) => p && p.kind === STOP;
export const isRoad = (p) => p && p.kind === ROAD;

/** Pontos de paragem de uma etapa (markers editáveis). */
export function stopsOf(seg) {
  const pts = seg && Array.isArray(seg.points) ? seg.points : [];
  return pts.filter(isStop);
}

/** Pontos de estrada (shaping) de uma etapa. */
export function roadsOf(seg) {
  const pts = seg && Array.isArray(seg.points) ? seg.points : [];
  return pts.filter(isRoad);
}

/**
 * Resolve texto multilingue (`I18nText` = string | {pt,en,es,fr}) para um
 * idioma, com fallback. Devolve "" se vazio.
 */
export function localizedText(value, lang = "pt", fallbackLang = "pt") {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value[lang] || value[fallbackLang] || value.pt || value.en
      || Object.values(value).find((v) => typeof v === "string" && v.trim()) || "";
  }
  return "";
}

/**
 * Lista de waypoints para a chamada a `/api/routes/directions`.
 * Mapeia: kind="road" → {via:true}; restantes → waypoint normal
 * (kind="stop" é obrigatório por ausência de `via`).
 *
 * Fase B (2026-06-19): delega em `buildRoutingWaypoints` para respeitar o
 * orçamento de 25 waypoints do motor (afina pontos de estrada por
 * Douglas-Peucker). Devolve só o array (compat. com os callers existentes).
 */
export function toRoutingWaypoints(points) {
  return buildRoutingWaypoints(points).waypoints;
}

const _wpOf = (p) => {
  const wp = { lat: p.lat, lng: p.lng };
  const addr = typeof p.address === "string" ? p.address.trim()
    : (typeof p.name === "string" ? p.name.trim() : "");
  if (addr) wp.address = addr;
  if (isRoad(p)) wp.via = true;
  return wp;
};

/** Distância perpendicular (plano lat/lng) do ponto P ao segmento A→B. */
function _perpDist(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) {
    const ex = px - ax, ey = py - ay;
    return Math.sqrt(ex * ex + ey * ey);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  const ex = px - cx, ey = py - cy;
  return Math.sqrt(ex * ex + ey * ey);
}

/**
 * Ranking de significância Douglas-Peucker: para cada ponto, a distância
 * perpendicular ao segmento em que foi o mais afastado (maior = mais
 * significativo para a forma). Extremos = Infinity (nunca descartados).
 */
function _dpSignificance(coords) {
  const n = coords.length;
  const sig = new Array(n).fill(0);
  if (n === 0) return sig;
  sig[0] = Infinity;
  sig[n - 1] = Infinity;
  if (n <= 2) return sig;
  const stack = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop();
    if (b - a < 2) continue;
    const ax = coords[a].lng, ay = coords[a].lat;
    const bx = coords[b].lng, by = coords[b].lat;
    let maxD = -1, idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = _perpDist(coords[i].lng, coords[i].lat, ax, ay, bx, by);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (idx >= 0) {
      sig[idx] = maxD;
      stack.push([a, idx], [idx, b]);
    }
  }
  return sig;
}

/**
 * Constrói os waypoints para `/api/routes/directions` respeitando o limite
 * `max` (Google: 25 incl. origem/destino). Regras (Fase B, §B):
 *   • Pontos OBRIGATÓRIOS (paragens `stop`, extremos, e qualquer ponto sem
 *     `kind`) são SEMPRE mantidos, na ordem original.
 *   • Pontos de ESTRADA (`road`, hint `via:true`) preenchem o orçamento
 *     restante; se sobrarem, mantém-se os mais significativos por
 *     Douglas-Peucker e descartam-se os redundantes.
 *   • Se os obrigatórios já excederem `max` → `tooManyStops:true` (o caller
 *     avisa o utilizador; mantém-se os obrigatórios em ordem na mesma).
 * Devolve `{ waypoints, droppedRoadCount, tooManyStops }`.
 */
export function buildRoutingWaypoints(points, max = 25) {
  const pts = (Array.isArray(points) ? points : [])
    .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length <= max) {
    return { waypoints: pts.map(_wpOf), droppedRoadCount: 0, tooManyStops: false };
  }
  const mandatory = pts.map((p) => !isRoad(p)); // stops + extremos + geometria
  const mandatoryCount = mandatory.filter(Boolean).length;
  const roadIdx = pts.map((_, i) => i).filter((i) => isRoad(pts[i]));
  if (mandatoryCount >= max) {
    // Pode ser (a) demasiadas paragens reais, ou (b) um TRACK cru com
    // geometria densa (kind ausente, sem nome). No caso (b) reduzimos a
    // geometria por Douglas-Peucker mantendo SEMPRE extremos + paragens
    // nomeadas — é isto que torna "Recalcular"/converter possível numa
    // gravação (sem isto seriam >25 waypoints e o motor recusava).
    const isHard = (p, i) =>
      isStop(p) || (typeof p.name === "string" && p.name.trim())
      || i === 0 || i === pts.length - 1;
    const hardIdx = pts.map((_, i) => i).filter((i) => isHard(pts[i], i));
    const softIdx = pts
      .map((_, i) => i)
      .filter((i) => !isHard(pts[i], i) && !isRoad(pts[i]));
    if (hardIdx.length >= max || softIdx.length === 0) {
      const kept = pts.filter((_, i) => mandatory[i]);
      return {
        waypoints: kept.map(_wpOf),
        droppedRoadCount: roadIdx.length,
        tooManyStops: mandatoryCount > max,
      };
    }
    const geomBudget = max - hardIdx.length;
    const sig = _dpSignificance(pts);
    const rankedSoft = softIdx.slice().sort((a, b) => sig[b] - sig[a]);
    const keepSoft = new Set(rankedSoft.slice(0, geomBudget));
    const keepIdx = new Set([...hardIdx, ...keepSoft]);
    const out = pts.filter((_, i) => keepIdx.has(i));
    return {
      waypoints: out.map(_wpOf),
      droppedRoadCount: roadIdx.length,
      tooManyStops: false,
    };
  }
  const roadBudget = max - mandatoryCount;
  if (roadIdx.length <= roadBudget) {
    return { waypoints: pts.map(_wpOf), droppedRoadCount: 0, tooManyStops: false };
  }
  const sig = _dpSignificance(pts);
  const rankedRoads = roadIdx.slice().sort((a, b) => sig[b] - sig[a]);
  const keepRoad = new Set(rankedRoads.slice(0, roadBudget));
  const out = pts.filter((_, i) => mandatory[i] || keepRoad.has(i));
  return {
    waypoints: out.map(_wpOf),
    droppedRoadCount: roadIdx.length - roadBudget,
    tooManyStops: false,
  };
}

/**
 * Converte a forma LEGACY ({points, waypoints, pois}) para a lista
 * unificada `points[]` com `kind`. Usado no import GPX/URL e (durante a
 * transição) ao reabrir rotas. Regras:
 *   • waypoints com `via:true`        → kind:"road".
 *   • waypoints com nome (via:false)  → kind:"stop" (name preservado).
 *   • pois (PDIs)                     → kind:"stop" (name+category),
 *                                       inseridos na posição do trajeto mais
 *                                       próxima dos `points` base.
 *   • restantes points sem nome/flag  → geometria (kind ausente).
 * `idxOf` encaixa um PDI no índice da geometria mais próxima.
 */
export function pointsFromLegacy({ points = [], waypoints = [], pois = [] } = {}) {
  const finite = (p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
  const matchLL = (a, b) =>
    Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;

  // Base = `points` (geometria/routing). Anota cada um com kind a partir
  // dos waypoints correspondentes (por coordenada).
  const wpts = (Array.isArray(waypoints) ? waypoints : []).filter(finite);
  const base = (Array.isArray(points) ? points : []).filter(finite).map((p) => {
    const wp = wpts.find((w) => matchLL(w, p));
    if (wp) {
      if (wp.via === true) {
        return { lat: p.lat, lng: p.lng, kind: ROAD };
      }
      const nm = wp.name || p.name || null;
      if (nm) {
        return {
          lat: p.lat, lng: p.lng, kind: STOP,
          name: nm, category: wp.category || p.category || "other",
          address: p.address || wp.address || null,
          poi_kind: wp.poi_kind || p.poi_kind || null,
        };
      }
    }
    // Sem waypoint associado: se o próprio ponto tem nome → stop; senão geometria.
    if (p.name) {
      return { lat: p.lat, lng: p.lng, kind: STOP, name: p.name, category: p.category || "other", address: p.address || null };
    }
    if (p.via === true) return { lat: p.lat, lng: p.lng, kind: ROAD };
    return { lat: p.lat, lng: p.lng };
  });

  // Encaixa os PDIs como stops na posição mais próxima ao longo de `base`.
  const nearestIdx = (item) => {
    let best = 0, bestD = Infinity;
    for (let i = 0; i < base.length; i++) {
      const dLat = base[i].lat - item.lat;
      const dLng = base[i].lng - item.lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  };
  const poiStops = (Array.isArray(pois) ? pois : [])
    .filter(finite)
    .map((poi) => ({
      _i: base.length ? nearestIdx(poi) : 0,
      stop: {
        lat: poi.lat, lng: poi.lng, kind: STOP,
        id: poi.id || null,
        name: poi.name || null,
        category: poi.category || "other",
        description: poi.description || null,
        photos: Array.isArray(poi.photos) ? poi.photos : [],
      },
    }))
    // inserir do fim para o início para não estragar os índices
    .sort((a, b) => b._i - a._i);

  const out = base.slice();
  for (const { _i, stop } of poiStops) {
    out.splice(Math.min(_i + 1, out.length), 0, stop);
  }
  // Garante id estável em TODAS as paragens (import GPX/URL não traz id) —
  // necessário para chavear editar/remover e os data-testid por paragem.
  return out.map((p) => (p.kind === STOP && !p.id ? { ...p, id: genStopId() } : p));
}

/**
 * Marca os extremos de uma lista `points[]` como stops com role start/end.
 * (start = primeiro; end = último). Garante que são sempre pontos de paragem.
 */
export function tagEndpoints(points) {
  const pts = Array.isArray(points) ? points.slice() : [];
  if (pts.length === 0) return pts;
  // Limpa qualquer role start/end "preso" em pontos INTERIORES — ex.: o
  // fim antigo de uma etapa, depois de se inserir um novo fim (after_end),
  // deixa de ser extremo e deve render como paragem normal (via) em vez de
  // ficar escondido (filtro do painel exclui role end/start) ou duplicado.
  for (let i = 0; i < pts.length; i++) {
    if (pts[i] && pts[i].role) pts[i] = { ...pts[i], role: undefined };
  }
  const first = pts[0];
  const last = pts[pts.length - 1];
  pts[0] = { ...first, kind: STOP, role: "start", id: first.id || genStopId() };
  pts[pts.length - 1] = { ...last, kind: STOP, role: "end", id: last.id || genStopId(), ...(pts.length === 1 ? { role: "start" } : {}) };
  return pts;
}
